import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { yardApis } from "@/features/relayYardControl/services/yardsApis";
import type { YardJob, WebsocketJobMessage } from "@/features/relayYardControl/types/jobs";
import {
  YardLocationType,
  YardVisit,
  WebsocketVisitMessage,
} from "@/features/relayYardControl/types/visits";
import {
  getLocationCode,
  getVisitId,
  processBatchUpdatesWithoutRaceConditions,
  RECORD_TYPES,
  updateItemWithLocationChange,
} from "@/features/relayYardControl/store/yardDetectionData/utils/utils";
import { YardAlert, WebsocketAlertMessage } from "@/features/relayYardControl/types/alert";
import {
  SECTION_TYPES,
  SectionType,
} from "@/pages/relayYardControl/AccuracyView/components/DetailPanel/constants/sectionTypes";
import { SEAL_FIELD_PREFIX } from "@/features/relayYardControl/constants";
import { isUnknownDestination } from "@/features/relayYardControl/constants/locationCodes";
import {
  findVisitByEquipmentId,
  applyEquipmentFieldChanges,
  applySaveApiResponses,
  applyOperationalCodesUpdate,
  updateAlertInState,
  SaveApiResponse,
} from "@/features/relayYardControl/store/yardDetectionData/visitTransformers";

export const LOCATION_DATA_KEYS = {
  VISITS: "visits",
  JOBS: "jobs",
  ALERTS: "alerts",
} as const;

export type LocationDataKey = keyof typeof LOCATION_DATA_KEYS;

export interface LocationData {
  visits: YardVisit[];
  alerts?: YardAlert[];
  jobs?: YardJob[];
}

export interface YardDetectionDataState {
  data: Record<string, LocationData>;
}

/**
 * Optimistically patch a single visit's TDR state in the store.
 * Used after emergencyStatusUpdate succeeds — avoids waiting for a refetch
 * that may return stale data due to backend propagation delay.
 */
function patchVisitTdrState(
  data: Record<string, LocationData>,
  visitId: string,
  tdrStatus: string
): void {
  for (const loc of Object.values(data)) {
    if (!loc?.visits) continue;

    const visit = loc.visits.find((v: YardVisit) => v.visitId === visitId);
    if (!visit?.data?.equipment) continue;

    const equipment = visit.data.equipment as Record<string, unknown>;
    equipment.tdrHandoverDetails = {
      ...(equipment.tdrHandoverDetails as Record<string, unknown> | undefined),
      tdrState: tdrStatus,
    };
    return;
  }
}

/**
 * Translate the move's destination into a `YardLocationType`.
 *
 * `MapLocationType` keys ("DOCK_DOOR", "PARKING_SLIP") match the
 * `YardLocationType` keys 1:1, so a direct index lookup works for
 * those. Two values need special handling:
 *   - `MapLocationType.GATE` is ambiguous (entry vs exit). We
 *     disambiguate via the sentinel location codes used by the backend
 *     (`EXIT_GATE` / `ENTRY_GATE`).
 *   - `MapLocationType.NO_GO_AREA` isn't a valid asset destination, so
 *     it falls through to `undefined` (caller leaves `location.type`
 *     untouched).
 */
const resolveDestinationLocationType = (
  destinationLocationCode: string,
  destinationMapLocationType: string | undefined
): YardLocationType | undefined => {
  if (destinationLocationCode === "EXIT_GATE") return YardLocationType.EXIT_GATE;
  if (destinationLocationCode === "ENTRY_GATE") return YardLocationType.ENTRY_GATE;
  if (!destinationMapLocationType) return undefined;
  return YardLocationType[destinationMapLocationType as keyof typeof YardLocationType];
};

const initialState: YardDetectionDataState = {
  data: {},
};

/**
 * Remove a visit from every location bucket. The store is keyed by
 * locationCode, but a delete only carries the visitId. Shared by `deleteVisit`
 *  (WebSocket) and `removeVisitById` (audit-remove flows).
 */
const removeVisitFromAllBuckets = (state: YardDetectionDataState, visitId: string): void => {
  for (const locationData of Object.values(state.data)) {
    if (!locationData?.visits) continue;
    locationData.visits = locationData.visits.filter((v) => v.visitId !== visitId);
  }
};

const yardDetectionDataSlice = createSlice({
  name: "yardDetectionData",
  initialState,
  reducers: {
    clearYardDetectionData: (state) => {
      state.data = {};
    },
    deleteVisit: (state, action: PayloadAction<WebsocketVisitMessage>) => {
      // `sortId` is the canonical visit id on every frame (DELETE markers carry
      // no `record`, so it's the only id available). Delegate to removeVisitFromAllBuckets,
      // which scans every bucket — the marker never carries a location, and the
      // same id can live in more than one bucket (e.g. a stale displaced dup).
      const { sortId } = action.payload;
      if (!sortId) return;
      removeVisitFromAllBuckets(state, sortId);
    },
    updateVisit: (state, action: PayloadAction<WebsocketVisitMessage>) => {
      // UPDATE frames always carry a record (unlike DELETE markers).
      if (!action.payload.record) return;
      // Clone the record since WebSocket objects may be frozen/non-extensible
      const visit = { ...action.payload.record };
      // Normalize visitId to top level (WebSocket records only have it nested)
      if (!visit.visitId && visit.data?.equipment?.visitDetails?.visitId?.id) {
        visit.visitId = visit.data.equipment.visitDetails.visitId.id;
      }
      const updatedState = updateItemWithLocationChange({
        state,
        item: visit,
        key: LOCATION_DATA_KEYS.VISITS,
        getLocationCodeFn: (item) => getLocationCode(item, RECORD_TYPES.VISIT),
        getId: getVisitId,
      });
      state.data = updatedState.data;
    },
    updateAlert: (state, action: PayloadAction<WebsocketAlertMessage>) => {
      const alert = action.payload.record;
      const updatedState = updateItemWithLocationChange({
        state,
        item: alert,
        key: LOCATION_DATA_KEYS.ALERTS,
        getLocationCodeFn: (item) => getLocationCode(item as YardAlert, RECORD_TYPES.ALERT),
        getId: (item) => (item as YardAlert).data.yardAlertId,
      });
      state.data = updatedState.data;
    },
    deleteAlert: (state, action: PayloadAction<WebsocketAlertMessage>) => {
      const alert = action.payload.record;
      const locationCode = getLocationCode(alert, RECORD_TYPES.ALERT) as string;
      const yardAlertId = alert.data.yardAlertId;
      if (yardAlertId) {
        if (state.data[locationCode]?.alerts) {
          state.data[locationCode].alerts = state.data[locationCode]?.alerts?.filter(
            (alert) => alert.data.yardAlertId !== yardAlertId
          );
        }
      }
    },
    updateJob: (state, action: PayloadAction<WebsocketJobMessage>) => {
      const job = action.payload.record;
      const updatedState = updateItemWithLocationChange({
        state,
        item: job,
        key: LOCATION_DATA_KEYS.JOBS,
        getLocationCodeFn: (item) => getLocationCode(item, RECORD_TYPES.JOB),
        getId: (item) => (item as YardJob).jobId,
      });
      state.data = updatedState.data;
    },
    deleteJob: (state, action: PayloadAction<WebsocketJobMessage>) => {
      const job = action.payload.record;
      const locationCode = getLocationCode(job, RECORD_TYPES.JOB);
      const jobId = job.jobId;

      if (locationCode && jobId) {
        if (state.data[locationCode].jobs) {
          state.data[locationCode].jobs = state.data[locationCode]?.jobs?.filter(
            (job) => job.jobId !== jobId
          );
        }
      }
    },
    completeAlert: (state, action: PayloadAction<any>) => {
      updateAlertInState(state, action.payload);
    },
    dismissAlert: (state, action: PayloadAction<any>) => {
      updateAlertInState(state, action.payload);
    },
    /**
     * Generic action to update section data after a successful save.
     * Uses API response data when available for authoritative state updates.
     * Falls back to changes-based updates for location saves (no API response).
     */
    updateSectionData: (
      state,
      action: PayloadAction<{
        sectionType: SectionType;
        locationCode: string;
        sectionId: string;
        changes: Record<string, unknown>;
        apiResponse?: unknown;
      }>
    ) => {
      const { sectionType, locationCode, sectionId, changes, apiResponse } = action.payload;
      const location = state.data[locationCode];

      if (!location) {
        return;
      }

      switch (sectionType) {
        case SECTION_TYPES.LOCATION: {
          if (changes.locationStatus === "empty") {
            location.visits = [];
          }
          break;
        }

        case SECTION_TYPES.TRAILER:
        case SECTION_TYPES.EQUIPMENT: {
          const visit = findVisitByEquipmentId(location, sectionId);
          if (!visit) break;

          if (apiResponse) {
            // Use authoritative API responses to update visit data
            applySaveApiResponses(visit, apiResponse as SaveApiResponse);
          } else {
            // Fallback: apply changes manually
            applyEquipmentFieldChanges(visit, changes);
          }

          // Also handle operational codes in the changes (fallback path)
          if (changes.operationalCodes !== undefined) {
            applyOperationalCodesUpdate(visit, changes.operationalCodes as string[]);
          }
          break;
        }

        case SECTION_TYPES.TRAILER_STATUS: {
          const visit = findVisitByEquipmentId(location, sectionId);
          if (!visit) break;

          if (apiResponse) {
            applySaveApiResponses(visit, apiResponse as SaveApiResponse);
          } else {
            // Fallback: apply seal changes manually
            const sealKeys = Object.keys(changes)
              .filter((key) => key.startsWith(SEAL_FIELD_PREFIX))
              .sort();

            if (sealKeys.length > 0) {
              const equipment = visit.data?.equipment;
              if (equipment) {
                equipment.actualSeals = sealKeys
                  .map((key) => ({
                    number: String(changes[key]).trim(),
                  }))
                  .filter((seal) => seal.number);
              }
            }
          }
          break;
        }

        case SECTION_TYPES.NOTES: {
          const visit = findVisitByEquipmentId(location, sectionId);
          if (!visit) break;

          const equipment = visit.data?.equipment;
          if (equipment && changes.notes !== undefined) {
            equipment.note = String(changes.notes);
          }
          break;
        }

        default:
          break;
      }
    },
    /**
     * Removes a visit from yard data — used by Reject CI/CO, Checking Out,
     * and Displaced flows where YMS calls `auditRemoveEquipmentFromYard`
     * (no destination — equipment is gone from the yard entirely).
     *
     * Filters every bucket because the same visitId can legitimately
     * appear under either a real location code or the
     * `EQUIPMENT_IS_DISPLACED` pseudo-bucket (`getLocationCode` returns
     * the displaced code when `data.location` is null and
     * `data.isDisplaced` is true). A stale duplicate from an in-flight
     * WebSocket race would also be cleared.
     */
    removeVisitById: (state, action: PayloadAction<{ visitId: string }>) => {
      removeVisitFromAllBuckets(state, action.payload.visitId);
    },
    /**
     * Called after a successful updateEquipmentLocation API call, before WebSocket confirms.
     *
     * NOTE: Race condition with WebSocket — the next WebSocket message will run through
     * processBatchUpdatesWithoutRaceConditions which uses datapathViewVersion to deduplicate.
     * A brief duplicate at the destination is possible if the WebSocket update arrives before
     * the next render cycle, but it self-corrects on the following WebSocket batch since the
     * version-based merge replaces stale entries. This is acceptable for instant UI feedback.
     */
    moveEquipment: (
      state,
      action: PayloadAction<{
        visitIds: string[];
        sourceLocationCode: string;
        destinationLocationCode: string;
        /**
         * The destination's `MapLocationType` from the yard map (e.g.
         * `"DOCK_DOOR"`, `"PARKING_SLIP"`). Translated to
         * `YardLocationType` here via the shared enum keys so derived
         * selectors (e.g. `computeOperationalActivityData`'s ExitGate
         * filter) stop bucketing the visit at the source on this render.
         * `MapLocationType.GATE` / `NO_GO_AREA` aren't valid asset
         * destinations and resolve to `undefined`, in which case the
         * existing `location.type` is left alone.
         */
        destinationMapLocationType?: string;
      }>
    ) => {
      const { visitIds, sourceLocationCode, destinationLocationCode, destinationMapLocationType } =
        action.payload;
      const visitIdSet = new Set(visitIds);

      const sourceLocation = state.data[sourceLocationCode];
      if (!sourceLocation?.visits) return;

      // Extract the visits to move
      const visitsToMove = sourceLocation.visits.filter((v) => visitIdSet.has(v.visitId));
      if (visitsToMove.length === 0) return;

      // Remove from source
      sourceLocation.visits = sourceLocation.visits.filter((v) => !visitIdSet.has(v.visitId));

      // Ensure destination location exists
      if (!state.data[destinationLocationCode]) {
        state.data[destinationLocationCode] = { visits: [] };
      }
      if (!state.data[destinationLocationCode].visits) {
        state.data[destinationLocationCode].visits = [];
      }

      const destinationType = resolveDestinationLocationType(
        destinationLocationCode,
        destinationMapLocationType
      );

      // The Displaced modal keys off `data.isDisplaced`, not the bucket.
      // Substring match keeps this in sync with the form-side predicate.
      const movingToUnknown = isUnknownDestination(destinationLocationCode);

      visitsToMove.forEach((visit) => {
        if (visit.data?.location?.identifier) {
          visit.data.location.identifier.locationCode = destinationLocationCode;
        }
        if (visit.data?.location && destinationType) {
          visit.data.location.type = destinationType;
        }
        if (visit.data) {
          visit.data.isDisplaced = movingToUnknown;
        }
        state.data[destinationLocationCode].visits.push(visit);
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        yardApis.endpoints.getYardAlerts.matchFulfilled,
        (state, { payload }: { payload: any[] | undefined }) => {
          processBatchUpdatesWithoutRaceConditions({
            state,
            items: payload || [],
            key: LOCATION_DATA_KEYS.ALERTS,
            getLocationCode: (item) => getLocationCode(item, RECORD_TYPES.ALERT),
            getId: (item) => (item as YardAlert).data.yardAlertId,
            processItem: (item) => ({
              ...item,
              datapathViewVersion: item?.datapathViewVersion || "0",
            }),
          });
        }
      )
      .addMatcher(
        yardApis.endpoints.getYardVisits.matchFulfilled,
        (state, { payload }: { payload: any[] | undefined }) => {
          processBatchUpdatesWithoutRaceConditions({
            state,
            items: payload || [],
            key: LOCATION_DATA_KEYS.VISITS,
            getLocationCode: (item) => getLocationCode(item, RECORD_TYPES.VISIT),
            getId: (item) => getVisitId(item as YardVisit),
            processItem: (item) => ({
              ...item,
              visitId: (item as YardVisit).visitId || getVisitId(item as YardVisit) || "",
              datapathViewVersion: item?.datapathViewVersion || "0",
            }),
          });
        }
      )
      .addMatcher(
        yardApis.endpoints.getYardJobs.matchFulfilled,
        (state, { payload }: { payload: any[] | undefined }) => {
          processBatchUpdatesWithoutRaceConditions({
            state,
            items: payload || [],
            key: LOCATION_DATA_KEYS.JOBS,
            getLocationCode: (item) => getLocationCode(item, RECORD_TYPES.JOB),
            getId: (item) => (item as YardJob).jobId,
            processItem: (item) => ({
              ...item,
              datapathViewVersion: item?.datapathViewVersion || "0",
            }),
          });
        }
      )
      // Optimistic TDR update — see patchVisitTdrState above.
      .addMatcher(yardApis.endpoints.emergencyStatusUpdate.matchFulfilled, (state, { meta }) => {
        const { visitId, newState } = meta.arg.originalArgs as {
          visitId?: string;
          newState?: { tdrStatus?: string };
        };
        if (visitId && newState?.tdrStatus) {
          patchVisitTdrState(state.data, visitId, newState.tdrStatus);
        }
      });
  },
});

export const {
  updateVisit,
  deleteVisit,
  clearYardDetectionData,
  updateJob,
  deleteJob,
  updateAlert,
  deleteAlert,
  completeAlert,
  dismissAlert,
  updateSectionData,
  removeVisitById,
  moveEquipment,
} = yardDetectionDataSlice.actions;

export default yardDetectionDataSlice.reducer;
